/**
 * W6 — Trajectory plan. The planner authors survey STATIONS (MD · inclination ·
 * azimuth) in an editable grid; the SERVER computes min-curvature TVD / N-E /
 * DLS and returns them (pApi `putTrajectory` PUT → computed stations). The
 * computed columns render read-only (provenance = computed) beside the editable
 * triple, and two hand-SVG views — a vertical section (TVD vs. horizontal
 * displacement) and a plan / N-E view — redraw from the returned stations. Wired
 * to the pApi `trajectory` dataset (GET datasets / PUT trajectory), MSW-seeded.
 *
 * Built to w6-trajectory-plan.html on the P3 kit. Trajectory applies to events
 * that drill a wellbore (P / S); light events (pulling / workover / CT) render
 * the NA state, matching eventEnrichment's SECTION_MATRIX.
 *
 * ★ Every API-data iteration goes through `asArray` / `deriveStations` at the
 * ingestion boundary — never `?? []` / truthy `if`. A present-but-non-array
 * `trajectory` (`{}`, `{stations:null}`, an error envelope) coerces to the empty
 * state, not a `.map` white-screen (the Oxbo-Z prod crash class).
 */
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { useGetPlanQuery, useGetPlanDatasetsQuery, usePutTrajectoryMutation } from "@api/pApi";
import type { TrajectoryStationOut } from "@api/types";
import { QueryState } from "@components/ui/QueryState";
import { InlineRowGrid, type GridColumn } from "@components/input/InlineRowGrid";
import { enrichEventCode, sectionApplies, EVENT_LETTER_OP_VAR } from "@lib/eventEnrichment";
import { fmtNum } from "@lib/format";
import {
  deriveStations,
  trajectoryTotals,
  sectionPoints,
  planPoints,
  toInput,
  emptyStation,
  DLS_WARN,
  type DerivedStation,
  type TrajectorySegment,
} from "./trajectoryModel";
import { VerticalSectionChart } from "./VerticalSectionChart";
import { PlanViewChart } from "./PlanViewChart";
import "./trajectory.css";

/** Fallback plan the screen opens against when W0 hasn't set an active plan. */
const DEFAULT_PLAN_ID = "pl-fp1216-pnc";

export function TrajectoryScreen() {
  const { t, locale } = useLocale();
  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);
  const planId = activePlanId ?? DEFAULT_PLAN_ID;

  const planQ = useGetPlanQuery(planId);
  const datasetsQ = useGetPlanDatasetsQuery(planId);
  const [saveTrajectory, saveState] = usePutTrajectoryMutation();

  // Local editable copy of the stations; the server dataset seeds it and a
  // re-fetch (new plan / discard) reseeds it. The computed columns (TVD/N-E/DLS)
  // are the SERVER's — an in-app edit clears them to null until the next save
  // re-computes, so the grid never shows a stale computed value against a fresh
  // MD/inclination.
  const [rows, setRows] = useState<TrajectoryStationOut[]>([]);
  const [dirty, setDirty] = useState(false);

  // asArray at the ingestion boundary (via deriveStations): a real backend can
  // return a present-but-non-array `trajectory` (`{}`, `null`, error envelope)
  // that a truthy `if (serverRows)` would admit — the next derive `.map` would
  // white-screen the app (the Oxbo-Z prod crash class).
  const serverRows = datasetsQ.data?.trajectory;
  useEffect(() => {
    setRows(deriveStations(serverRows).map(toDatasetRow));
    setDirty(false);
  }, [serverRows]);

  const derived = useMemo(() => deriveStations(rows), [rows]);
  const totals = useMemo(() => trajectoryTotals(rows), [rows]);
  const section = useMemo(() => sectionPoints(rows), [rows]);
  const plan = useMemo(() => planPoints(rows), [rows]);

  const planMeta = planQ.data;
  const enr = enrichEventCode(planMeta?.event_code);
  const opVar = enr.letter ? EVENT_LETTER_OP_VAR[enr.letter] : "--op-unconv";
  const applies = sectionApplies("hole_plan", enr.letter, enr.isConventional);

  function commit(next: DerivedStation[]) {
    // An edit invalidates the SERVER-computed columns until the next save; strip
    // them so the read-only cells show "—" rather than a stale TVD/DLS.
    setRows(
      next.map((r) => ({ ...toInput(r), tvd: null, northing: null, easting: null, dls: null })),
    );
    setDirty(true);
  }

  async function save() {
    // Send only the editable triple; the server returns the computed stations.
    const stations = deriveStations(rows).map(toInput);
    await saveTrajectory({ planId, stations }).unwrap();
    setDirty(false);
  }

  const columns: GridColumn<DerivedStation>[] = [
    {
      key: "segment",
      label: t("traj.col.segment"),
      width: 96,
      render: (r) => <SegTag seg={r.segment} label={t(`traj.seg.${r.segment}`)} />,
    },
    editNumCol("md", t("traj.col.md")),
    editNumCol("inclination", t("traj.col.inc")),
    editNumCol("azimuth", t("traj.col.azi")),
    computedCol("tvd", t("traj.col.tvd"), locale),
    computedCol("northing", t("traj.col.ns"), locale),
    computedCol("easting", t("traj.col.ew"), locale),
    {
      key: "dls",
      label: t("traj.col.dls"),
      numeric: true,
      render: (r) => (
        <span
          className={`tab-nums ${r.dls != null && r.dls >= DLS_WARN ? "warn" : "dim"}`}
          title={t("traj.computed")}
        >
          {r.dls == null ? "—" : fmtNum(r.dls, locale, { maximumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="screen-head">
        <h1>{t("traj.title")}</h1>
        <span className="badge info">{t("traj.badge")}</span>
        <div className="actions">
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={!dirty || saveState.isLoading}
          >
            <span className="ic ic-check" aria-hidden="true" />
            {saveState.isLoading ? t("traj.computing") : t("traj.save")}
          </button>
        </div>
      </div>

      {/* event / plan context */}
      {planMeta && (
        <div className="chip-ctx" style={{ marginBottom: 16, flexWrap: "wrap" }}>
          <span className="k">{t("traj.ctx.well")}</span>
          <span className="mono">{planMeta.well_uwi}</span>
          <span className="dim">·</span>
          <span className="k">{t("traj.ctx.event")}</span>
          <span className="letter-badge" style={{ background: `var(${opVar})` }}>
            <span className="sw" style={{ background: "var(--head)" }} />
            {enr.letter ?? "?"} · {planMeta.event_code}
          </span>
          {planMeta.afe_no && (
            <>
              <span className="dim">·</span>
              <span className="k">{t("traj.ctx.afe")}</span>
              <span className="mono">{planMeta.afe_no}</span>
            </>
          )}
          <span className="verchip">v{planMeta.version}</span>
        </div>
      )}

      <div className="section-note">
        <span className="qm">i</span>
        <span>{t("traj.note")}</span>
      </div>

      <QueryState
        isLoading={datasetsQ.isLoading}
        isError={datasetsQ.isError}
        onRetry={() => void datasetsQ.refetch()}
      >
        {!applies ? (
          <div className="card empty" style={{ padding: 28 }}>
            <strong>{t("traj.na.title")}</strong>
            <p className="dim" style={{ marginTop: 6, fontSize: 13 }}>
              {t("traj.na.body")}
            </p>
          </div>
        ) : (
          <>
            {/* KPI chips */}
            <div className="kpis">
              <Kpi v={fmtNum(totals.stations, locale)} l={t("traj.kpi.stations")} own="p" />
              <Kpi v={fmtNum(totals.finalMd, locale)} u="m" l={t("traj.kpi.finalMd")} own="p" />
              <Kpi v={fmtNum(totals.targetTvd, locale)} u="m" l={t("traj.kpi.targetTvd")} own="p" />
              <Kpi v={fmtNum(totals.kopMd, locale)} u="m" l={t("traj.kpi.kop")} own="p" />
              <Kpi
                v={fmtNum(totals.lateralDisplacement, locale, { maximumFractionDigits: 0 })}
                u="m"
                l={t("traj.kpi.displacement")}
                own="z"
              />
              <Kpi
                v={fmtNum(totals.maxDls, locale, { maximumFractionDigits: 2 })}
                u="°/30m"
                l={t("traj.kpi.maxDls")}
                own="z"
                warn={totals.maxDls != null && totals.maxDls >= DLS_WARN}
              />
            </div>

            <div className="traj-grid">
              {/* LEFT: editable survey-station grid */}
              <section className="card" style={{ padding: 16 }}>
                <div className="rowflex" style={{ marginBottom: 10 }}>
                  <h2 style={{ margin: 0 }}>
                    {t("traj.grid.title")}{" "}
                    <span className="dim" style={{ fontWeight: 400, fontSize: 13 }}>
                      — {t("traj.grid.hint")}
                    </span>
                  </h2>
                </div>

                <InlineRowGrid<DerivedStation>
                  rows={derived}
                  columns={columns}
                  onChange={(next) => commit(next)}
                  makeEmptyRow={(i) => deriveStations([emptyStation(i)])[0]!}
                  addLabel={t("traj.addStation")}
                  emptyLabel={t("traj.empty")}
                />
                <p className="dim" style={{ fontSize: 11.5, margin: "8px 2px 0" }}>
                  {t("traj.computeHint")}
                </p>
              </section>

              {/* RIGHT: the two hand-SVG trajectory views */}
              <div className="traj-views">
                <section className="card" style={{ padding: 16 }}>
                  <div className="rowflex" style={{ marginBottom: 6 }}>
                    <h2 style={{ margin: 0, fontSize: 15 }}>{t("traj.section.title")}</h2>
                    <span className="dim" style={{ fontSize: 12 }}>
                      {t("traj.section.sub")}
                    </span>
                  </div>
                  {section.length > 0 ? (
                    <>
                      <VerticalSectionChart
                        points={section}
                        kopTvd={totals.kopMd != null ? kopTvdOf(derived) : null}
                        kopLabel={
                          totals.kopMd != null
                            ? `${t("traj.kop")} ${fmtNum(totals.kopMd, locale)} m`
                            : undefined
                        }
                        xLabel={t("traj.section.xLabel")}
                        yLabel={t("traj.section.yLabel")}
                        ariaLabel={t("traj.section.aria")}
                      />
                      <Legend />
                    </>
                  ) : (
                    <p className="dim" style={{ fontSize: 13, padding: "18px 4px" }}>
                      {t("traj.section.empty")}
                    </p>
                  )}
                </section>

                <section className="card" style={{ padding: 16 }}>
                  <div className="rowflex" style={{ marginBottom: 6 }}>
                    <h2 style={{ margin: 0, fontSize: 15 }}>{t("traj.plan.title")}</h2>
                    <span className="dim" style={{ fontSize: 12 }}>
                      {t("traj.plan.sub")}
                    </span>
                  </div>
                  {plan.length > 0 ? (
                    <>
                      <PlanViewChart
                        points={plan}
                        xLabel={t("traj.plan.xLabel")}
                        yLabel={t("traj.plan.yLabel")}
                        ariaLabel={t("traj.plan.aria")}
                      />
                      <Legend />
                    </>
                  ) : (
                    <p className="dim" style={{ fontSize: 13, padding: "18px 4px" }}>
                      {t("traj.plan.empty")}
                    </p>
                  )}
                </section>
              </div>
            </div>

            <p className="dim" style={{ fontSize: 12, marginTop: 16, lineHeight: 1.7 }}>
              {t("traj.footnote")}
            </p>
          </>
        )}
      </QueryState>
    </div>
  );
}

/** The deepest-before-lateral station's TVD, used to place the KOP marker line. */
function kopTvdOf(derived: DerivedStation[]): number | null {
  const kop = derived.find((s) => s.inclination > 3);
  // Place the marker at the station JUST before the first non-vertical one (the
  // last truly-vertical TVD), falling back to the KOP station's own TVD.
  const idx = kop ? derived.indexOf(kop) : -1;
  if (idx <= 0) return kop?.tvd ?? null;
  return derived[idx - 1]?.tvd ?? kop?.tvd ?? null;
}

/** Map a derived station back to the dataset (out) row shape for local state. */
function toDatasetRow(s: DerivedStation): TrajectoryStationOut {
  return {
    seq: s.seq,
    md: s.md,
    inclination: s.inclination,
    azimuth: s.azimuth,
    tvd: s.tvd,
    northing: s.northing,
    easting: s.easting,
    dls: s.dls,
  };
}

/** An editable numeric cell bound to one of the input triple fields. */
function editNumCol(
  key: "md" | "inclination" | "azimuth",
  label: string,
): GridColumn<DerivedStation> {
  return {
    key,
    label,
    numeric: true,
    render: (r, update) => (
      <input
        className="cell tab-nums"
        inputMode="decimal"
        style={{ textAlign: "right" }}
        value={r[key] == null ? "" : String(r[key])}
        aria-label={label}
        placeholder="0"
        onChange={(e) => {
          const raw = e.target.value.trim();
          const next = raw === "" ? 0 : Number(raw.replace(/,/g, ""));
          update({ [key]: Number.isFinite(next) ? next : r[key] } as Partial<DerivedStation>);
        }}
      />
    ),
  };
}

/** A read-only, server-computed cell (provenance = computed). */
function computedCol(
  key: "tvd" | "northing" | "easting",
  label: string,
  locale: Parameters<typeof fmtNum>[1],
): GridColumn<DerivedStation> {
  return {
    key,
    label,
    numeric: true,
    render: (r) => (
      <span className="dim tab-nums" title="computed">
        {r[key] == null ? "—" : fmtNum(r[key], locale, { maximumFractionDigits: 1 })}
      </span>
    ),
  };
}

function SegTag({ seg, label }: { seg: TrajectorySegment; label: string }) {
  return <span className={`seg-tag ${seg}`}>{label}</span>;
}

function Legend() {
  const { t } = useLocale();
  return (
    <div className="legend">
      <span className="s" style={{ ["--series" as string]: "var(--dim)" }}>
        {t("traj.seg.vertical")}
      </span>
      <span className="s" style={{ ["--series" as string]: "var(--op-unconv)" }}>
        {t("traj.seg.build")}
      </span>
      <span className="s" style={{ ["--series" as string]: "var(--op-drilling)" }}>
        {t("traj.seg.lateral")}
      </span>
    </div>
  );
}

function Kpi({
  v,
  u,
  l,
  own,
  warn,
}: {
  v: string;
  u?: string;
  l: string;
  own: "p" | "z";
  warn?: boolean;
}) {
  return (
    <div className="kpi" data-own={own}>
      <div className={`v tab-nums ${warn ? "warn" : ""}`}>
        {v}
        {u && <span style={{ fontSize: 12, color: "var(--dim)" }}> {u}</span>}
      </div>
      <div className="l">{l}</div>
    </div>
  );
}
